import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { apiFetch } from "../api/api";

/**
 * NOTE:
 * - We DO NOT show any IDs in columns.
 * - IDs are still present in row objects for edit/update.
 * - asset_sub_class + sub_subclass list APIs should return JOIN fields:
 *   asset_name, subclass_name
 * - etf_master list API should return JOIN fields:
 *   asset_name, subclass_name, subsubclass_name, amcname, biname
 */
const ENTITIES = [
  {
    key: "amc",
    title: "AMC",
    pk: "amcid",
    fields: [{ name: "amcname", label: "AMC Name", type: "text", required: true }],
    columns: [{ field: "amcname", headerName: "AMC Name", flex: 1, minWidth: 260 }],
  },
  {
    key: "benchmarkindex",
    title: "Benchmark Index",
    pk: "biid",
    fields: [
      { name: "biname", label: "Index Name", type: "text", required: true },
      { name: "is_public", label: "Public Ranking", type: "bool" },
    ],
    columns: [
      { field: "biname", headerName: "Index Name", flex: 1, minWidth: 260 },
      {
        field: "is_public",
        headerName: "Public Ranking",
        width: 170,
        renderCell: (params) => (params.row?.is_public ? "Yes" : "No"),
      },
    ],
  },
  {
    key: "asset_class",
    title: "Asset Class",
    pk: "assetid",
    fields: [
      { name: "asset_name", label: "Asset Name", type: "text", required: true },
      { name: "has_subclass", label: "Has Subclass", type: "bool" },
    ],
    columns: [
      { field: "asset_name", headerName: "Asset Name", flex: 1, minWidth: 260 },
      {
        field: "has_subclass",
        headerName: "Has Subclass",
        width: 160,
        // ✅ FIX: DataGrid v7 safe access
        renderCell: (params) => (params.row?.has_subclass ? "Yes" : "No"),
      },
    ],
  },
  {
    key: "asset_sub_class",
    title: "Asset Sub Class",
    pk: "assetsubid",
    filterBy: ["assetid"],
    fields: [
      { name: "assetid", label: "Asset Class", type: "select_asset", required: true },
      { name: "subclass_name", label: "Subclass Name", type: "text", required: true },
      { name: "has_subclass", label: "Has Subclass", type: "bool" },
    ],
    columns: [
      { field: "asset_name", headerName: "Asset Class", flex: 1, minWidth: 240 },
      { field: "subclass_name", headerName: "Subclass Name", flex: 1, minWidth: 240 },
      {
        field: "has_subclass",
        headerName: "Has Subclass",
        width: 160,
        // ✅ FIX: DataGrid v7 safe access
        renderCell: (params) => (params.row?.has_subclass ? "Yes" : "No"),
      },
    ],
  },
  {
    key: "sub_subclass",
    title: "Sub Subclass",
    pk: "subsubclassid",
    filterBy: ["assetid", "assetsubid"],
    fields: [
      { name: "assetid", label: "Asset Class", type: "select_asset", required: true },
      { name: "assetsubid", label: "Asset Sub Class", type: "select_assetsub", required: true },
      { name: "subsubclass_name", label: "SubSub Name", type: "text", required: true },
    ],
    columns: [
      { field: "asset_name", headerName: "Asset Class", flex: 1, minWidth: 220 },
      { field: "subclass_name", headerName: "Sub Class", flex: 1, minWidth: 220 },
      { field: "subsubclass_name", headerName: "SubSub Name", flex: 1, minWidth: 220 },
    ],
  },

  // -----------------------------
  // ETF Master Entry
  // -----------------------------
  {
    key: "etf_master",
    title: "ETF Master",
    pk: "etf_id",
    filterBy: ["assetid", "subclassid", "sub_subclassid", "amcid", "biid"],
    fields: [
      { name: "assetid", label: "Asset Class", type: "select_asset", required: true },
      { name: "subclassid", label: "Asset Sub Class", type: "select_subclassid", required: true },
      { name: "sub_subclassid", label: "Sub Subclass", type: "select_subsubclassid" },

      { name: "amcid", label: "AMC", type: "select_amc", required: true },
      { name: "biid", label: "Benchmark Index", type: "select_benchmark", required: true },

      { name: "etf_name", label: "ETF Name", type: "text", required: true },
      { name: "symbol", label: "Symbol", type: "text", required: true },
      { name: "bse_ticker", label: "BSE Ticker", type: "num" },
      { name: "isin_code", label: "ISIN Code", type: "text" },
      { name: "website_link", label: "Website Link", type: "text", required: true },

      { name: "launch_date", label: "Launch Date", type: "date", required: true },
      { name: "aum_crores", label: "AUM (Crores)", type: "num" },
      { name: "expense_ratio", label: "Expense Ratio", type: "num", required: true },
      { name: "overall_tracking_error", label: "Overall Tracking Error", type: "num" },
      { name: "overall_tracking_difference", label: "Overall Tracking Difference", type: "num" },
      { name: "years_active", label: "Years Active", type: "num", required: true },
    ],
    columns: [
      { field: "asset_name", headerName: "Asset Class", flex: 1, minWidth: 200 },
      { field: "subclass_name", headerName: "Sub Class", flex: 1, minWidth: 220 },
      { field: "subsubclass_name", headerName: "Sub Subclass", flex: 1, minWidth: 220 },
      { field: "amcname", headerName: "AMC", flex: 1, minWidth: 180 },
      { field: "biname", headerName: "Benchmark", flex: 1, minWidth: 220 },

      { field: "etf_name", headerName: "ETF Name", flex: 1.5, minWidth: 260 },
      { field: "symbol", headerName: "Symbol", width: 140 },
      { field: "isin_code", headerName: "ISIN", width: 160 },
      { field: "launch_date", headerName: "Launch", width: 120 },

      { field: "aum_crores", headerName: "AUM", width: 110 },
      {
        field: "expense_ratio",
        headerName: "Expense Ratio",
        width: 130,
        renderCell: (params) => fmtDecimal(params.row?.expense_ratio, 4),
      },
      { field: "overall_tracking_error", headerName: "TE", width: 90 },
      { field: "overall_tracking_difference", headerName: "TD", width: 90 },
      { field: "years_active", headerName: "Years", width: 90 },

      {
        field: "website_link",
        headerName: "Website",
        flex: 1.2,
        minWidth: 240,
        renderCell: (params) => {
          const v = params.row?.website_link;
          if (!v) return "";
          const href = String(v);
          return (
            <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              {href}
            </a>
          );
        },
      },
    ],
  },
];

const initForm = (cfg) => {
  const o = {};
  cfg.fields.forEach((f) => {
    if (f.type === "bool") o[f.name] = false;
    else o[f.name] = "";
  });
  return o;
};

const fmtDecimal = (value, digits = 2) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const buildPayload = (cfg, form) => {
  const payload = {};
  cfg.fields.forEach((f) => (payload[f.name] = form[f.name]));
  return payload;
};

export default function Masters() {
  const [tab, setTab] = useState(0);
  const cfg = ENTITIES[tab];
  const pk = cfg.pk;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // dropdowns
  const [dd, setDd] = useState({
    asset_class: [],
    asset_sub_class: [],
    sub_subclass: [],
    amc: [],
    benchmarkindex: [],
  });

  // filters (support all keys used by any tab)
  const [filters, setFilters] = useState({
    assetid: "",
    assetsubid: "",
    subclassid: "",
    sub_subclassid: "",
    amcid: "",
    biid: "",
  });

  // search
  const [q, setQ] = useState("");

  // dialog
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add"); // add | edit
  const [form, setForm] = useState(initForm(cfg));

  const assetOptions = dd.asset_class || [];

  const assetSubOptions = useMemo(() => {
    const all = dd.asset_sub_class || [];
    if (!filters.assetid) return all;
    return all.filter((x) => String(x.assetid) === String(filters.assetid));
  }, [dd.asset_sub_class, filters.assetid]);

  const decorateRows = (dataRows) =>
    (dataRows || []).map((r) => ({
      ...r,
      id: r[pk], // DataGrid internal id
    }));

  const fetchDropdowns = async () => {
    try {
      const data = await apiFetch("/api/v1/auth/etf/master-drpdowns");
      setDd({
        asset_class: data.asset_class || [],
        asset_sub_class: data.asset_sub_class || [],
        sub_subclass: data.sub_subclass || [],
        amc: data.amc || [],
        benchmarkindex: data.benchmarkindex || [],
      });
    } catch {
      // optional
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();

      (cfg.filterBy || []).forEach((k) => {
        const v = filters?.[k];
        if (v) qs.set(k, v);
      });

      const url = `/api/v1/auth/etf/master/${cfg.key}${qs.toString() ? `?${qs.toString()}` : ""}`;
      const data = await apiFetch(url);

      setRows(decorateRows(data.rows || []));
    } catch (e) {
      toast.error(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reset on tab switch
  useEffect(() => {
    setQ("");
    setFilters({
      assetid: "",
      assetsubid: "",
      subclassid: "",
      sub_subclassid: "",
      amcid: "",
      biid: "",
    });
    setRows([]);
    setForm(initForm(cfg));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // refetch on tab + filters
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    filters.assetid,
    filters.assetsubid,
    filters.subclassid,
    filters.sub_subclassid,
    filters.amcid,
    filters.biid,
  ]);

  // auto-clear ETF sub_subclassid if selected subclass doesn't support it
  useEffect(() => {
    if (cfg.key !== "etf_master") return;
    const sub = (dd.asset_sub_class || []).find((x) => String(x.assetsubid) === String(form.subclassid));
    const has = Boolean(sub?.has_subclass);
    if (!has && form.sub_subclassid) {
      setForm((p) => ({ ...p, sub_subclassid: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.key, dd.asset_sub_class, form.subclassid]);

  const filteredRows = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) =>
      Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(t))
    );
  }, [rows, q]);

  const openAdd = () => {
    setMode("add");
    setForm(initForm(cfg));
    setOpen(true);
  };

  const openEdit = (row) => {
    setMode("edit");
    const clean = initForm(cfg);

    // keep pk for update
    clean[pk] = row[pk];

    // keep only DB fields
    cfg.fields.forEach((f) => {
      clean[f.name] = row[f.name] ?? clean[f.name];
    });

    setForm(clean);
    setOpen(true);
  };

  const validate = () => {
    for (const f of cfg.fields) {
      if (!f.required) continue;
      const v = form[f.name];
      if (v === null || v === undefined || String(v).trim() === "") {
        toast.error(`${f.label} is required`);
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;

    try {
      const payload = buildPayload(cfg, form);

      if (mode === "add") {
        await apiFetch(`/api/v1/auth/admin/etf/master/${cfg.key}`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.success("Saved");
      } else {
        await apiFetch(`/api/v1/auth/admin/etf/master/${cfg.key}/${form[pk]}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.success("Updated");
      }

      setOpen(false);
      await fetchDropdowns();
      await fetchList();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const renderFilters = () => {
    if (!cfg.filterBy?.length) return null;

    // build sub-sub options based on filters
    const pickSubId = filters.subclassid || filters.assetsubid;
    const subSubOptions = (dd.sub_subclass || []).filter((x) => {
      if (filters.assetid && String(x.assetid) !== String(filters.assetid)) return false;
      if (pickSubId && String(x.assetsubid) !== String(pickSubId)) return false;
      return true;
    });

    return (
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5, flexWrap: "wrap" }}>
        {cfg.filterBy.includes("assetid") && (
          <TextField
            select
            label="Asset Class"
            size="small"
            value={filters.assetid}
            onChange={(e) =>
              setFilters((p) => ({
                ...p,
                assetid: e.target.value,
                assetsubid: "",
                subclassid: "",
                sub_subclassid: "",
              }))
            }
            sx={{ minWidth: 260 }}
          >
            <MenuItem value="">All</MenuItem>
            {assetOptions.map((a) => (
              <MenuItem key={a.assetid} value={a.assetid}>
                {a.asset_name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {cfg.filterBy.includes("assetsubid") && (
          <TextField
            select
            label="Asset Sub Class"
            size="small"
            value={filters.assetsubid}
            onChange={(e) =>
              setFilters((p) => ({
                ...p,
                assetsubid: e.target.value,
              }))
            }
            sx={{ minWidth: 280 }}
            disabled={!filters.assetid}
          >
            <MenuItem value="">All</MenuItem>
            {assetSubOptions.map((s) => (
              <MenuItem key={s.assetsubid} value={s.assetsubid}>
                {s.subclass_name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {cfg.filterBy.includes("subclassid") && (
          <TextField
            select
            label="Asset Sub Class"
            size="small"
            value={filters.subclassid}
            onChange={(e) =>
              setFilters((p) => ({
                ...p,
                subclassid: e.target.value,
                sub_subclassid: "",
              }))
            }
            sx={{ minWidth: 280 }}
            disabled={!filters.assetid}
          >
            <MenuItem value="">All</MenuItem>
            {assetSubOptions.map((s) => (
              <MenuItem key={s.assetsubid} value={s.assetsubid}>
                {s.subclass_name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {cfg.filterBy.includes("sub_subclassid") && (
          <TextField
            select
            label="Sub Subclass"
            size="small"
            value={filters.sub_subclassid}
            onChange={(e) => setFilters((p) => ({ ...p, sub_subclassid: e.target.value }))}
            sx={{ minWidth: 280 }}
            disabled={!filters.assetid || !(filters.subclassid || filters.assetsubid)}
          >
            <MenuItem value="">All</MenuItem>
            {subSubOptions.map((s) => (
              <MenuItem key={s.subsubclassid} value={s.subsubclassid}>
                {s.subsubclass_name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {cfg.filterBy.includes("amcid") && (
          <TextField
            select
            label="AMC"
            size="small"
            value={filters.amcid}
            onChange={(e) => setFilters((p) => ({ ...p, amcid: e.target.value }))}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All</MenuItem>
            {(dd.amc || []).map((a) => (
              <MenuItem key={a.amcid} value={a.amcid}>
                {a.amcname}
              </MenuItem>
            ))}
          </TextField>
        )}

        {cfg.filterBy.includes("biid") && (
          <TextField
            select
            label="Benchmark"
            size="small"
            value={filters.biid}
            onChange={(e) => setFilters((p) => ({ ...p, biid: e.target.value }))}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">All</MenuItem>
            {(dd.benchmarkindex || []).map((b) => (
              <MenuItem key={b.biid} value={b.biid}>
                {b.biname}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
    );
  };

  const renderField = (f) => {
    const val = form[f.name];

    if (f.type === "bool") {
      return (
        <FormControlLabel
          key={f.name}
          label={f.label}
          control={
            <Switch
              checked={Boolean(val)}
              onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.checked }))}
            />
          }
        />
      );
    }

    if (f.type === "select_asset") {
      return (
        <TextField
          key={f.name}
          select
          label={f.label}
          size="small"
          fullWidth
          required={Boolean(f.required)}
          value={val || ""}
          onChange={(e) => {
            const next = e.target.value;
            setForm((p) => ({
              ...p,
              assetid: next,
              ...(cfg.key === "sub_subclass" ? { assetsubid: "" } : {}),
              ...(cfg.key === "etf_master" ? { subclassid: "", sub_subclassid: "" } : {}),
            }));
          }}
        >
          <MenuItem value="">Select</MenuItem>
          {assetOptions.map((a) => (
            <MenuItem key={a.assetid} value={a.assetid}>
              {a.asset_name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (f.type === "select_assetsub") {
      const list = (dd.asset_sub_class || []).filter((x) =>
        form.assetid ? String(x.assetid) === String(form.assetid) : true
      );

      return (
        <TextField
          key={f.name}
          select
          label={f.label}
          size="small"
          fullWidth
          required={Boolean(f.required)}
          value={val || ""}
          onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
          disabled={!form.assetid}
        >
          <MenuItem value="">Select</MenuItem>
          {list.map((s) => (
            <MenuItem key={s.assetsubid} value={s.assetsubid}>
              {s.subclass_name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (f.type === "select_subclassid") {
      const list = (dd.asset_sub_class || []).filter((x) =>
        form.assetid ? String(x.assetid) === String(form.assetid) : true
      );

      return (
        <TextField
          key={f.name}
          select
          label={f.label}
          size="small"
          fullWidth
          required={Boolean(f.required)}
          value={val || ""}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              subclassid: e.target.value,
              sub_subclassid: "",
            }))
          }
          disabled={!form.assetid}
        >
          <MenuItem value="">Select</MenuItem>
          {list.map((s) => (
            <MenuItem key={s.assetsubid} value={s.assetsubid}>
              {s.subclass_name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (f.type === "select_subsubclassid") {
      const list = (dd.sub_subclass || []).filter((x) => {
        if (form.assetid && String(x.assetid) !== String(form.assetid)) return false;
        if (form.subclassid && String(x.assetsubid) !== String(form.subclassid)) return false;
        return true;
      });

      const sub = (dd.asset_sub_class || []).find((x) => String(x.assetsubid) === String(form.subclassid));
      const has = Boolean(sub?.has_subclass);

      return (
        <TextField
          key={f.name}
          select
          label={f.label}
          size="small"
          fullWidth
          required={Boolean(f.required)}
          value={val || ""}
          onChange={(e) => setForm((p) => ({ ...p, sub_subclassid: e.target.value }))}
          disabled={!form.assetid || !form.subclassid || !has}
          helperText={!has && form.subclassid ? "Selected subclass has no sub-subclass" : " "}
        >
          <MenuItem value="">Select</MenuItem>
          {list.map((s) => (
            <MenuItem key={s.subsubclassid} value={s.subsubclassid}>
              {s.subsubclass_name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (f.type === "select_amc") {
      return (
        <TextField
          key={f.name}
          select
          label={f.label}
          size="small"
          fullWidth
          required={Boolean(f.required)}
          value={val || ""}
          onChange={(e) => setForm((p) => ({ ...p, amcid: e.target.value }))}
        >
          <MenuItem value="">Select</MenuItem>
          {(dd.amc || []).map((a) => (
            <MenuItem key={a.amcid} value={a.amcid}>
              {a.amcname}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (f.type === "select_benchmark") {
      return (
        <TextField
          key={f.name}
          select
          label={f.label}
          size="small"
          fullWidth
          required={Boolean(f.required)}
          value={val || ""}
          onChange={(e) => setForm((p) => ({ ...p, biid: e.target.value }))}
        >
          <MenuItem value="">Select</MenuItem>
          {(dd.benchmarkindex || []).map((b) => (
            <MenuItem key={b.biid} value={b.biid}>
              {b.biname}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (f.type === "date") {
      return (
        <TextField
          key={f.name}
          label={f.label}
          size="small"
          fullWidth
          required={Boolean(f.required)}
          type="date"
          InputLabelProps={{ shrink: true }}
          value={val || ""}
          onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
        />
      );
    }

    if (f.type === "num") {
      return (
        <TextField
          key={f.name}
          label={f.label}
          size="small"
          fullWidth
          required={Boolean(f.required)}
          type="number"
          inputProps={{ step: f.name === "expense_ratio" ? "0.0001" : "any" }}
          value={val ?? ""}
          onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
        />
      );
    }

    return (
      <TextField
        key={f.name}
        label={f.label}
        size="small"
        fullWidth
        required={Boolean(f.required)}
        value={val ?? ""}
        onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
      />
    );
  };

  const columns = useMemo(() => {
    return [
      ...cfg.columns,
      {
        field: "__actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Edit">
            <IconButton onClick={() => openEdit(params.row)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ];
  }, [cfg]);

  return (
    <Box sx={{ p: 2 }}>
      <ToastContainer />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Masters
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Public masters
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{ minWidth: { xs: 220, md: 320 } }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchList}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
              Add
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2, opacity: 0.4 }} />

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {ENTITIES.map((e) => (
            <Tab key={e.key} label={e.title} />
          ))}
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
          {cfg.title}
        </Typography>

        {renderFilters()}

        <div style={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
          />
        </div>
      </Paper>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={cfg.key === "etf_master" ? "md" : "sm"}
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {mode === "add" ? `Add ${cfg.title}` : `Edit ${cfg.title}`}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {cfg.fields.map(renderField)}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button variant="contained" onClick={save}>
            {mode === "add" ? "Save" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
